'use client';

import {
  DEFAULT_FEEDBACK_SCREEN_CONTENT,
  DEFAULT_PAYOUT_BACKEND_DATA,
} from '@/components/payouts/constants/payoutConstants';
import { Nullable } from '@/components/types';
import {
  FeedbackScreenContent,
  NotificationData,
  PayoutBackendData,
} from '@/types/sharedInformation';
import React, { createContext, ReactNode, useContext, useState } from 'react';

// Simple noop function instead of importing from lodash
const noop = (): void => {};

interface SharedInformationContextProps {
  eventId: string;
  setEventId: (eventId: string) => void;
  organizationId: string;
  setOrganizationId: (organizationId: string) => void;
  amountToWithdraw: number;
  setAmountToWithdraw: (amount: number) => void;
  payoutBackendData: PayoutBackendData;
  setPayoutBackendData: (data: PayoutBackendData) => void;
  feedbackScreenContent: FeedbackScreenContent;
  setFeedbackScreenContent: (content: FeedbackScreenContent) => void;
  notificationData: Nullable<NotificationData>;
  setNotificationData: (data: Nullable<NotificationData>) => void;
}

const SharedInformationContext = createContext<SharedInformationContextProps>({
  eventId: '',
  setEventId: noop,
  organizationId: '',
  setOrganizationId: noop,
  amountToWithdraw: 0,
  setAmountToWithdraw: noop,
  payoutBackendData: DEFAULT_PAYOUT_BACKEND_DATA,
  setPayoutBackendData: noop,
  feedbackScreenContent: DEFAULT_FEEDBACK_SCREEN_CONTENT,
  setFeedbackScreenContent: noop,
  notificationData: null,
  setNotificationData: noop,
});

interface SharedInformationContextProviderProps {
  children: ReactNode;
}

/**
 * SharedInformationContextProvider - Provides shared state management for payout-related information
 *
 * This provider manages 6 state variables following the original eb-ui pattern:
 * - eventId: Current event ID
 * - organizationId: Current organization ID
 * - amountToWithdraw: Amount to withdraw
 * - payoutBackendData: Backend payout data
 * - feedbackScreenContent: Content for feedback screens
 * - notificationData: Notification information
 *
 * @param children - Child components that will have access to the context
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <SharedInformationContextProvider>
 *       <MyComponent />
 *     </SharedInformationContextProvider>
 *   );
 * }
 * ```
 */
export const SharedInformationContextProvider: React.FC<SharedInformationContextProviderProps> = ({
  children,
}) => {
  // State variables matching original eb-ui implementation
  const [eventId, setEventId] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>('');
  const [amountToWithdraw, setAmountToWithdraw] = useState<number>(0);
  const [payoutBackendData, setPayoutBackendData] = useState<PayoutBackendData>(
    DEFAULT_PAYOUT_BACKEND_DATA,
  );
  const [feedbackScreenContent, setFeedbackScreenContent] = useState<FeedbackScreenContent>(
    DEFAULT_FEEDBACK_SCREEN_CONTENT,
  );
  const [notificationData, setNotificationData] = useState<Nullable<NotificationData>>(null);

  const contextValue: SharedInformationContextProps = {
    eventId,
    setEventId,
    organizationId,
    setOrganizationId,
    amountToWithdraw,
    setAmountToWithdraw,
    payoutBackendData,
    setPayoutBackendData,
    feedbackScreenContent,
    setFeedbackScreenContent,
    notificationData,
    setNotificationData,
  };

  return (
    <SharedInformationContext.Provider value={contextValue}>
      {children}
    </SharedInformationContext.Provider>
  );
};

/**
 * Hook to access shared information context values
 *
 * Provides access to all 6 state variables and their setters from anywhere
 * in the component tree below SharedInformationContextProvider.
 *
 * @returns Object containing:
 * - `eventId`: Current event ID
 * - `setEventId`: Function to update event ID
 * - `organizationId`: Current organization ID
 * - `setOrganizationId`: Function to update organization ID
 * - `amountToWithdraw`: Current amount to withdraw
 * - `setAmountToWithdraw`: Function to update amount to withdraw
 * - `payoutBackendData`: Current payout backend data
 * - `setPayoutBackendData`: Function to update payout backend data
 * - `feedbackScreenContent`: Current feedback screen content
 * - `setFeedbackScreenContent`: Function to update feedback screen content
 * - `notificationData`: Current notification data
 * - `setNotificationData`: Function to update notification data
 *
 * @example
 * ```tsx
 * function PayoutComponent() {
 *   const {
 *     eventId,
 *     organizationId,
 *     amountToWithdraw,
 *     setAmountToWithdraw,
 *     payoutBackendData,
 *     setPayoutBackendData
 *   } = useSharedInformationContext();
 *
 *   const handlePayoutUpdate = async () => {
 *     try {
 *       const data = await fetchPayoutData(eventId, organizationId);
 *       setPayoutBackendData(data);
 *     } catch (err) {
 *       console.error('Payout update failed:', err);
 *     }
 *   };
 *
 *   return <div>Payout data: {payoutBackendData.payoutAmount}</div>;
 * }
 * ```
 */
export const useSharedInformationContext = (): SharedInformationContextProps => {
  return useContext(SharedInformationContext);
};
